import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getUsers, getCourses, getCertificates } from '../../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Button } from '../ui/button';
import { Users, BookOpen, Award, Shield, Settings, Plus, Image, FileText, UserCheck, Bot, LayoutGrid, HelpCircle, Inbox, Globe } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '../ui/badge';

export const AdminDashboard = () => {
  const [users, setUsers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, coursesRes, certsRes] = await Promise.all([
        getUsers(),
        getCourses(),
        getCertificates()
      ]);
      setUsers(usersRes.data);
      setCourses(coursesRes.data);
      setCertificates(certsRes.data);
    } catch (error) {
      toast.error('Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="p-8">Cargando...</div>;
  }

  return (
    <div className="p-6 md:p-12" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-heading font-bold text-primary mb-2">
            Panel de Administración
          </h1>
          <p className="text-muted-foreground">Gestiona usuarios, cursos y certificados</p>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Link to="/admin/courses">
            <Button className="w-full h-20 text-lg" variant="outline">
              <BookOpen className="w-6 h-6 mr-2" />
              Gestionar Cursos
            </Button>
          </Link>
          <Link to="/admin/certificates">
            <Button className="w-full h-20 text-lg" variant="outline">
              <Award className="w-6 h-6 mr-2" />
              Emitir Certificados
            </Button>
          </Link>
          <Link to="/admin/certificate-templates">
            <Button className="w-full h-20 text-lg" variant="outline">
              <FileText className="w-6 h-6 mr-2" />
              Plantillas
            </Button>
          </Link>
          <Link to="/admin/media">
            <Button className="w-full h-20 text-lg" variant="outline">
              <Image className="w-6 h-6 mr-2" />
              Medios
            </Button>
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Link to="/admin/delegates">
            <Button className="w-full h-16" variant="outline">
              <UserCheck className="w-5 h-5 mr-2" />
              Gestionar Delegados
            </Button>
          </Link>
          <Link to="/admin/ai-knowledge">
            <Button className="w-full h-16" variant="outline">
              <Bot className="w-5 h-5 mr-2" />
              AI Tutor - Conocimiento
            </Button>
          </Link>
          <Link to="/admin/users">
            <Button className="w-full h-16" variant="outline">
              <Users className="w-5 h-5 mr-2" />
              Gestionar Usuarios
            </Button>
          </Link>
          <Link to="/admin/cms">
            <Button className="w-full h-16" variant="outline">
              <LayoutGrid className="w-5 h-5 mr-2" />
              Editor CMS
            </Button>
          </Link>
          <Link to="/admin/pages/home">
            <Button className="w-full h-16" variant="outline" data-testid="admin-page-builder-btn">
              <LayoutGrid className="w-5 h-5 mr-2" />
              Page Builder
            </Button>
          </Link>
          <Link to="/admin/articles">
            <Button className="w-full h-16" variant="outline" data-testid="admin-articles-btn">
              <FileText className="w-5 h-5 mr-2" />
              Artículos
            </Button>
          </Link>
          <Link to="/admin/enrollment-requests">
            <Button className="w-full h-16" variant="outline" data-testid="admin-enrollment-requests-btn">
              <Inbox className="w-5 h-5 mr-2" />
              Solicitudes
            </Button>
          </Link>
          <Link to="/admin/country-contacts">
            <Button className="w-full h-16" variant="outline" data-testid="admin-country-contacts-btn">
              <Globe className="w-5 h-5 mr-2" />
              Contactos País
            </Button>
          </Link>
          <Link to="/admin/settings">
            <Button className="w-full h-16" variant="outline">
              <Settings className="w-5 h-5 mr-2" />
              Configuración
            </Button>
          </Link>
          <Link to="/admin/help">
            <Button className="w-full h-16" variant="outline">
              <HelpCircle className="w-5 h-5 mr-2" />
              Centro de Ayuda
            </Button>
          </Link>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Usuarios</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{users.length}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Cursos</CardTitle>
              <BookOpen className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{courses.length}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Certificados Emitidos</CardTitle>
              <Award className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{certificates.length}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Profesores</CardTitle>
              <Shield className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {users.filter(u => u.role === 'professor').length}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="users" className="w-full">
          <TabsList>
            <TabsTrigger value="users">Usuarios</TabsTrigger>
            <TabsTrigger value="courses">Cursos</TabsTrigger>
            <TabsTrigger value="certificates">Certificados</TabsTrigger>
          </TabsList>

          <TabsContent value="users">
            <Card>
              <CardHeader>
                <CardTitle>Usuarios</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {users.map((user) => (
                    <div 
                      key={user.id}
                      className="flex items-center justify-between p-4 border rounded-lg"
                      data-testid={`user-${user.id}`}
                    >
                      <div>
                        <h3 className="font-semibold">{user.name}</h3>
                        <p className="text-sm text-muted-foreground">{user.email}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge>{user.role}</Badge>
                        <Badge variant={user.status === 'active' ? 'success' : 'secondary'}>
                          {user.status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="courses">
            <Card>
              <CardHeader>
                <CardTitle>Cursos</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {courses.map((course) => (
                    <div 
                      key={course.id}
                      className="flex items-center justify-between p-4 border rounded-lg"
                      data-testid={`course-${course.id}`}
                    >
                      <div>
                        <h3 className="font-semibold">{course.title}</h3>
                        <p className="text-sm text-muted-foreground">
                          Instructor: {course.instructor_name}
                        </p>
                      </div>
                      <Badge>{course.status}</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="certificates">
            <Card>
              <CardHeader>
                <CardTitle>Certificados</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {certificates.map((cert) => (
                    <div 
                      key={cert.id}
                      className="flex items-center justify-between p-4 border rounded-lg"
                      data-testid={`certificate-${cert.id}`}
                    >
                      <div>
                        <h3 className="font-semibold">{cert.user_name}</h3>
                        <p className="text-sm text-muted-foreground">{cert.course_title}</p>
                        <p className="text-xs text-muted-foreground font-mono">{cert.code}</p>
                      </div>
                      <Badge variant={cert.status === 'valid' ? 'success' : 'destructive'}>
                        {cert.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;
